import type { ReactNode } from 'react';
import { DAY_NAMES, bucketLabel, formatCount, formatSeconds, hourLabel } from '../../lib/kyckConnect/insightFormat';
import { GROUPINGS, type Grouping } from '../../lib/kyckConnect/metrics';
import { ENQUIRIES_LABEL, ENQUIRY_NOTE } from '../../lib/leads/enquiries';
import { PolishedBarChart, PolishedGapLine, PolishedHeatmap, PolishedQualityChart, PolishedRowBars } from './PolishedCharts';
import { REPORT_MONTHS, type CallQualityPoint, type KyckConnectReport, type ReportMonths, type ReportPeriodData, type SeriesPoint } from '../../types/kyckConnectInsights';

// THE KYCKCONNECT REPORT, SHARED BY TWO HOSTS (portal Reporting in the Kyck Design System look, staff Reporting in the staff look).
// ⚠️ This component draws what the server pre-aggregated and decides nothing: a null speed is a GAP in the line (never a zero), an
// empty period says so, `capped` is printed, and which charts exist follows the services the server reported. Colours are theme tokens
// only (primary for fills): the page it sits in decides what primary looks like. It uses no chart library: plain SVG and CSS.

const FILL = 'fill-primary';

function Card({ title, note, testId, children }: { title: string; note?: string; testId: string; children: ReactNode }) {
  return (
    <section className="kc-card rounded-[18px] border border-border p-3" data-testid={testId}>
      <h3 className="kc-label text-muted-foreground">{title}</h3>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

const Empty = () => <p className="text-muted-foreground" data-testid="chart-empty">Nothing in this period yet.</p>;

/** Thin the axis so labels never collide: about eight, always including the first. */
function tickIndexes(n: number): Set<number> {
  const step = Math.max(1, Math.ceil(n / 8));
  const out = new Set<number>();
  for (let i = 0; i < n; i += step) out.add(i);
  return out;
}

const W = 600;
const H = 140;
const PAD = 18;

function Axis({ keys }: { keys: readonly string[] }) {
  const ticks = tickIndexes(keys.length);
  const slot = (W - PAD * 2) / Math.max(1, keys.length);
  return (
    <>
      {keys.map((k, i) =>
        ticks.has(i) ? (
          <text key={k} x={PAD + slot * i + slot / 2} y={H - 2} textAnchor="middle" className="fill-muted-foreground" fontSize="9">
            {bucketLabel(k)}
          </text>
        ) : null,
      )}
    </>
  );
}

/** Bars per bucket, with the previous period (when asked for) as a thin line over the same slots, matched by position. */
export function BarChart({ points, previous, label }: { points: readonly SeriesPoint[]; previous?: readonly SeriesPoint[] | null; label: string }) {
  const keys = points.map((p) => p.key);
  const prevValues = (previous ?? []).map((p) => p.value ?? 0);
  const max = Math.max(1, ...points.map((p) => p.value ?? 0), ...prevValues);
  const slot = (W - PAD * 2) / Math.max(1, points.length);
  const barH = (v: number): number => (v / max) * (H - PAD * 2);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={label} data-testid="bar-chart">
      {points.map((p, i) => {
        const v = p.value ?? 0;
        return <rect key={p.key} x={PAD + slot * i + slot * 0.12} y={H - PAD - barH(v)} width={slot * 0.76} height={Math.max(v === 0 ? 1 : 2, barH(v))} className={FILL} opacity={v === 0 ? 0.25 : 1}><title>{`${bucketLabel(p.key)}: ${v}`}</title></rect>;
      })}
      {previous && previous.length > 0 && (
        <polyline fill="none" strokeWidth="1.5" className="stroke-muted-foreground" data-testid="previous-line" points={prevValues.slice(0, points.length).map((v, i) => `${PAD + slot * i + slot / 2},${H - PAD - barH(v)}`).join(' ')} />
      )}
      <Axis keys={keys} />
    </svg>
  );
}

/** A line of medians. A null bucket BREAKS the line (no call dialled in it): it is never drawn as zero. */
export function GapLineChart({ points, label }: { points: readonly SeriesPoint[]; label: string }) {
  const values = points.map((p) => p.value);
  const max = Math.max(1, ...values.map((v) => v ?? 0));
  const slot = (W - PAD * 2) / Math.max(1, points.length);
  const x = (i: number): number => PAD + slot * i + slot / 2;
  const y = (v: number): number => H - PAD - (v / max) * (H - PAD * 2);
  const segments: string[][] = [];
  let cur: string[] = [];
  values.forEach((v, i) => {
    if (v === null) {
      if (cur.length) segments.push(cur);
      cur = [];
    } else cur.push(`${x(i)},${y(v)}`);
  });
  if (cur.length) segments.push(cur);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={label} data-testid="gap-line-chart">
      {segments.map((seg, i) => (seg.length === 1 ? <circle key={i} cx={seg[0]!.split(',')[0]} cy={seg[0]!.split(',')[1]} r="2.5" className={FILL} /> : <polyline key={i} fill="none" strokeWidth="2" className="stroke-primary" points={seg.join(' ')} />))}
      {values.map((v, i) => (v === null ? null : <circle key={points[i]!.key} cx={x(i)} cy={y(v)} r="2" className={FILL}><title>{`${bucketLabel(points[i]!.key)}: ${formatSeconds(v)}`}</title></circle>))}
      <text x={PAD} y={10} fontSize="9" className="fill-muted-foreground">{`top: ${formatSeconds(max)}`}</text>
      <Axis keys={points.map((p) => p.key)} />
    </svg>
  );
}

export function QualityChart({ points }: { points: readonly CallQualityPoint[] }) {
  const max = Math.max(1, ...points.map((p) => p.booked + p.goodConversation + p.notAFit));
  const slot = (W - PAD * 2) / Math.max(1, points.length);
  const h = (v: number): number => (v / max) * (H - PAD * 2);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Call quality by period" data-testid="quality-chart">
      {points.map((p, i) => {
        const x = PAD + slot * i + slot * 0.12;
        const w = slot * 0.76;
        let y = H - PAD;
        const seg = (v: number, cls: string, opacity: number) => {
          const hh = h(v);
          y -= hh;
          return hh > 0 ? <rect key={cls} x={x} y={y} width={w} height={hh} className={cls} opacity={opacity} /> : null;
        };
        return (
          <g key={p.key}>
            <title>{`${bucketLabel(p.key)}: ${p.booked} booked, ${p.goodConversation} good conversation, ${p.notAFit} not a fit`}</title>
            {seg(p.booked, 'fill-primary', 1)}
            {seg(p.goodConversation, 'fill-primary', 0.45)}
            {seg(p.notAFit, 'fill-muted-foreground', 0.35)}
          </g>
        );
      })}
      <Axis keys={points.map((p) => p.key)} />
    </svg>
  );
}

export function RowBars({ rows, empty = true }: { rows: Array<{ label: string; count: number; extra?: string }>; empty?: boolean }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (rows.length === 0) return empty ? <Empty /> : null;
  return (
    <ul className="space-y-1" data-testid="row-bars">
      {rows.map((r) => (
        <li key={r.label} className="flex items-center gap-2">
          <span className="w-40 shrink-0 truncate text-foreground">{r.label}</span>
          <span className="h-3 rounded-sm bg-primary" style={{ width: `${Math.max(2, (r.count / max) * 100)}%` }} />
          <span className="tabular-nums text-muted-foreground">{r.count}{r.extra ? ` ${r.extra}` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

export function Heatmap({ cells }: { cells: ReadonlyArray<{ dow: number; hour: number; count: number }> }) {
  const max = Math.max(1, ...cells.map((c) => c.count));
  const at = (dow: number, hour: number): number => cells.find((c) => c.dow === dow && c.hour === hour)?.count ?? 0;
  const order = [1, 2, 3, 4, 5, 6, 0];
  return (
    <div className="overflow-x-auto" data-testid="heatmap">
      <table className="border-separate border-spacing-0.5 text-[10px]">
        <thead>
          <tr>
            <th />
            {Array.from({ length: 24 }, (_, h) => <th key={h} className="font-normal text-muted-foreground">{h % 3 === 0 ? hourLabel(h) : ''}</th>)}
          </tr>
        </thead>
        <tbody>
          {order.map((d) => (
            <tr key={d}>
              <th className="pr-1 text-right font-normal text-muted-foreground">{DAY_NAMES[d]}</th>
              {Array.from({ length: 24 }, (_, h) => {
                const n = at(d, h);
                return <td key={h} title={`${DAY_NAMES[d]} ${hourLabel(h)}: ${n}`} className="h-4 w-4 rounded-sm bg-primary" style={{ opacity: n === 0 ? 0.08 : 0.2 + 0.8 * (n / max) }} />;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface ReportControlsState {
  months: ReportMonths;
  grouping: Grouping;
  compare: boolean;
}

export function ReportControls({ value, onChange }: { value: ReportControlsState; onChange: (v: ReportControlsState) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="report-controls">
      <div className="flex gap-1" role="group" aria-label="Look back">
        {REPORT_MONTHS.map((m) => <button key={m} type="button" className="kc-tab kc-chip rounded-md border px-2 py-1 aria-pressed:bg-primary aria-pressed:text-primary-foreground" aria-pressed={value.months === m} data-active={value.months === m} onClick={() => onChange({ ...value, months: m })}>{m} months</button>)}
      </div>
      <div className="flex gap-1" role="group" aria-label="Group by">
        {GROUPINGS.map((g) => <button key={g} type="button" className="kc-tab kc-chip rounded-md border px-2 py-1 capitalize aria-pressed:bg-primary aria-pressed:text-primary-foreground" aria-pressed={value.grouping === g} data-active={value.grouping === g} onClick={() => onChange({ ...value, grouping: g })}>{g}</button>)}
      </div>
      <label className="flex items-center gap-1 text-foreground">
        <input type="checkbox" checked={value.compare} onChange={(e) => onChange({ ...value, compare: e.target.checked })} />
        Compare to the previous period
      </label>
    </div>
  );
}

const sum = (xs: readonly SeriesPoint[]): number => xs.reduce((n, p) => n + (p.value ?? 0), 0);

function Totals({ cur, prev }: { cur: ReportPeriodData; prev: ReportPeriodData | null }) {
  const row = (label: string, now: number, before: number | null) => (
    <div key={label}>
      <div className="kc-label text-muted-foreground">{label}</div>
      <div className="kc-numeral text-xl font-semibold text-foreground">{formatCount(now)}</div>
      {before !== null && <div className="text-xs text-muted-foreground" data-testid="previous-figure">Before: {formatCount(before)}</div>}
    </div>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-4" data-testid="report-totals">
      {row(ENQUIRIES_LABEL, cur.totals.leads, prev ? prev.totals.leads : null)}
      {row('Connected calls', cur.totals.connectedCalls, prev ? prev.totals.connectedCalls : null)}
      {row('Calls your team made', cur.totals.teamCalls, prev ? prev.totals.teamCalls : null)}
      {row('Booked by text', sum(cur.aiSmsBookings), prev ? sum(prev.aiSmsBookings) : null)}
    </div>
  );
}

/**
 * `polished` is the PORTAL's drawing (labelled y axes, one axis label size, a legend with totals, a heatmap that fills its card). Staff
 * leave it off and keep the plain charts, so the staff look does not change. The data and every honesty rule are the same either way.
 */
export function ReportView({ report, polished = false }: { report: KyckConnectReport; polished?: boolean }) {
  const { current: cur, previous: prev, services } = report;
  const any = services.leadRelay || services.aiSms.on || services.speedToLead;
  const empty = cur.totals.leads === 0 && cur.totals.connectedCalls === 0;
  return (
    <div className="space-y-4" data-testid="report-view">
      {report.capped && <p className="kc-card rounded-[12px] border border-border p-2 text-sm text-foreground" data-testid="report-capped">This period holds more than we can total at once, so these figures are a minimum.</p>}
      {!any && <p className="text-muted-foreground" data-testid="report-no-services">No KyckConnect service is switched on yet.</p>}
      {any && empty && <p className="text-muted-foreground" data-testid="report-empty">No leads or calls in this period.</p>}
      {any && (
        <>
          <Totals cur={cur} prev={prev} />
          <Card title="Leads over time" testId="chart-leads" note={`${ENQUIRY_NOTE}${prev ? ' The line is the previous period, matched by position.' : ''}`}>
            {polished ? <PolishedBarChart points={cur.leadsOverTime} previous={prev?.leadsOverTime} label="Leads over time" /> : <BarChart points={cur.leadsOverTime} previous={prev?.leadsOverTime} label="Leads over time" />}
          </Card>
          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Where leads come from" testId="chart-source">{polished ? <PolishedRowBars rows={cur.bySource} /> : <RowBars rows={cur.bySource} />}</Card>
            <Card title="When leads arrive" testId="chart-heatmap" note={`Day and hour in each club's own time (${report.timezone}).`}>{polished ? <PolishedHeatmap cells={cur.heatmap} /> : <Heatmap cells={cur.heatmap} />}</Card>
          </div>
        </>
      )}
      {services.speedToLead && (
        <>
          <Card title="Speed to lead" testId="chart-speed" note="Median time from the lead arriving to the lead being called. A gap means no call went out that period.">
            {polished ? <PolishedGapLine points={cur.speedTrend} label="Median speed to lead" /> : <GapLineChart points={cur.speedTrend} label="Median speed to lead" />}
          </Card>
          <Card title="Call quality" testId="chart-quality" note={`Graded by how the call ended, never by its length. ${cur.totals.gradedCalls} of ${cur.totals.connectedCalls} connected calls graded${cur.totals.aiSuggestedGraded > 0 ? `; ${cur.totals.aiSuggestedGraded} rest on the AI suggestion and are not yet confirmed` : ''}.`}>
            {polished ? <PolishedQualityChart points={cur.callQuality} /> : (
              <>
                <QualityChart points={cur.callQuality} />
                <p className="mt-1 text-xs text-muted-foreground">Booked, good conversation, not a fit (darkest to lightest).</p>
              </>
            )}
          </Card>
          <Card title="By staff member" testId="chart-staff">
            {(() => {
              const rows = cur.byStaff.map((s) => ({ label: s.name, count: s.answered, extra: s.booked > 0 ? `answered, ${s.booked} booked` : 'answered' }));
              return polished ? <PolishedRowBars rows={rows} /> : <RowBars rows={rows} />;
            })()}
          </Card>
        </>
      )}
      {services.aiSms.on && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="AI SMS replies" testId="chart-ai-replies" note="Leads who replied to a text, by the day they arrived.">{polished ? <PolishedBarChart points={cur.aiSmsReplies} previous={prev?.aiSmsReplies} label="AI SMS replies" /> : <BarChart points={cur.aiSmsReplies} previous={prev?.aiSmsReplies} label="AI SMS replies" />}</Card>
          <Card title="Booked by text" testId="chart-ai-bookings">{polished ? <PolishedBarChart points={cur.aiSmsBookings} previous={prev?.aiSmsBookings} label="Bookings made by text" /> : <BarChart points={cur.aiSmsBookings} previous={prev?.aiSmsBookings} label="Bookings made by text" />}</Card>
        </div>
      )}
    </div>
  );
}

